/**
 * B45 — the onboarding forward-CTA labels, and the ONE rule that decides which
 * label a screen wears.
 *
 * Same posture as `onboardingAgeCopy.ts` (REL-29) and `onboardingSaveCopy.ts`
 * (B26): no React, no router, no CSS in the graph, so the strings and the rule
 * are readable and pinnable on their own.
 *
 * ── THE BUG THIS FILE EXISTS TO PREVENT ───────────────────────────────────
 * Step 6 hardcoded "Meet my coaches" in all four journey variants, under a
 * comment declaring it "the last substantive step before the Welcome outcome
 * screen". That was true when it was written. It stopped being true when F-520
 * inserted the training-data beat AFTER step 6 and BEFORE Welcome: a fresh
 * signup is missing all three items, so the button promising the coaches reveal
 * was followed by one to three more questions. The founder reported it twice
 * on device.
 *
 * The lesson is that "which screen is last" is a property of the FLOW, not of
 * any one step component, and it moves every time a beat is inserted. So no
 * step may name the coaches reveal on its own authority. The route, which is
 * the only place that knows what is still queued, computes the label here.
 *
 * ── THE RULE ──────────────────────────────────────────────────────────────
 * A CTA may announce the coaches only when the coaches reveal is literally the
 * NEXT screen. Everything else says "Continue". One screen at most wears
 * `meetCoaches` in any given run of the wizard, and it is whichever question
 * happens to be last for THIS athlete.
 *
 * ⚠️ The two strings are existing shipped copy, reused verbatim. What is new
 * and PROVISIONAL pending founder ratification is WHERE "Meet my coaches" now
 * lands: on the training-data beat's final question for the common fresh
 * signup, and on step 6 only when nothing else is queued.
 */

export const ONBOARDING_CTA = {
  /** Any screen that still has a question after it. */
  continue: 'Continue',
  /** Reserved for the single last question before the step 7 coaches reveal. */
  meetCoaches: 'Meet my coaches',
} as const;

/**
 * Every wizard screen that owns a forward CTA, in flow order.
 *
 * The age step (REL-29) is deliberately absent: it renders above the wizard,
 * owns its own ratified copy in `onboardingAgeCopy.ts`, and can never be last.
 * The guardian beat is absent too, because it is not a question and offers
 * Skip and Continue as same-weight paths rather than one forward CTA.
 */
export type OnboardingCtaScreen =
  | 'step1_journey'
  | 'step2_location'
  | 'step3_parq'
  | 'step4_body_metrics'
  | 'step5_pace'
  | 'step6_focus'
  | 'data_beat_last_question';

/**
 * What is still queued between the screen being labelled and the coaches
 * reveal. Both flags are computed by the route from the same state its render
 * branches use, so the label cannot disagree with what actually renders next.
 */
export type OnboardingPendingBeats = {
  /** F-520 — the training-data beat (one to three questions) has yet to run. */
  dataBeatPending: boolean;
  /** UAT-B2 — the 13 to 15 guardian-invite beat has yet to run. */
  guardianBeatPending: boolean;
};

/**
 * Is the coaches reveal the very next screen after this one?
 *
 * Steps 1 to 5 are never last: step 6 always follows them. Step 6 is last only
 * when neither beat is queued. The data beat's final question is last only when
 * the guardian beat is not queued.
 */
export function isLastScreenBeforeCoaches(
  screen: OnboardingCtaScreen,
  pending: OnboardingPendingBeats,
): boolean {
  switch (screen) {
    case 'step1_journey':
    case 'step2_location':
    case 'step3_parq':
    case 'step4_body_metrics':
    case 'step5_pace':
      return false;
    case 'step6_focus':
      return !pending.dataBeatPending && !pending.guardianBeatPending;
    case 'data_beat_last_question':
      // Already inside the data beat, so only the guardian beat can still
      // stand between this question and the reveal.
      return !pending.guardianBeatPending;
    default: {
      // Exhaustiveness guard: a NEW screen added to the union without a case
      // here fails the type-check rather than silently inheriting a label.
      const never: never = screen;
      return never;
    }
  }
}

/** The forward-CTA label for a screen, given what is still queued behind it. */
export function onboardingCtaLabel(
  screen: OnboardingCtaScreen,
  pending: OnboardingPendingBeats,
): string {
  return isLastScreenBeforeCoaches(screen, pending)
    ? ONBOARDING_CTA.meetCoaches
    : ONBOARDING_CTA.continue;
}
